import type { LiveMonitorRun } from '@borradh-workspace/api-client/types';
import { formatDistanceToNowStrict } from 'date-fns';
import { Activity, Play } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

import {
  useLiveMonitor,
  useRunLiveMonitor,
  useSetLiveMonitor,
} from '../../api';

const ago = (iso: string) =>
  formatDistanceToNowStrict(new Date(iso), { addSuffix: true });

function RunRow({
  run,
  onShowFlagged,
}: {
  run: LiveMonitorRun;
  onShowFlagged: () => void;
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-sm">
      <span className="w-28 shrink-0 text-muted-foreground text-xs">
        {ago(run.startedAt)}
      </span>
      {run.error ? (
        <span className="text-rose-600 dark:text-rose-400">
          Failed: {run.error}
        </span>
      ) : (
        <>
          <span>{run.scored} checked</span>
          {run.flagged.length ? (
            <button
              type="button"
              onClick={onShowFlagged}
              className="font-medium text-rose-600 underline-offset-2 hover:underline dark:text-rose-400"
            >
              {run.flagged.length} flagged
            </button>
          ) : (
            <span className="text-muted-foreground">nothing flagged</span>
          )}
          {run.errors ? (
            <span className="text-amber-700 dark:text-amber-300">
              {run.errors} couldn’t be checked
            </span>
          ) : null}
        </>
      )}
      {run.trigger === 'manual' ? (
        <Badge variant="outline" className="font-normal">
          Run now
        </Badge>
      ) : null}
    </li>
  );
}

/**
 * The production monitor: one switch. Off by default; when on, every 10
 * minutes the API checks the replies customers received since the last run
 * against the evals.
 */
export function LiveMonitorCard({
  onShowFlagged,
}: {
  onShowFlagged: () => void;
}) {
  const { monitor, isLoading } = useLiveMonitor();
  const { setMonitor, isSaving } = useSetLiveMonitor();
  const { runNow, isRunning } = useRunLiveMonitor();

  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (!monitor) return null;

  const lastRun = monitor.runs[0];
  const status = !monitor.allowed
    ? monitor.unavailableReason
    : monitor.enabled
      ? `On: checks production every ${monitor.intervalMinutes} minutes.${
          lastRun ? ` Last run ${ago(lastRun.startedAt)}.` : ''
        }`
      : `Off. Switch on to check every new reply against the evals every ${monitor.intervalMinutes} minutes.`;

  return (
    <Card data-testid="live-monitor">
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <Switch
              id="live-monitor-switch"
              checked={monitor.enabled}
              disabled={!monitor.allowed || isSaving}
              onCheckedChange={(on) => setMonitor(on)}
            />
            <Label
              htmlFor="live-monitor-switch"
              className="flex items-center gap-1.5 font-medium"
            >
              <Activity
                className={cn(
                  'size-4',
                  monitor.enabled && monitor.allowed
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-muted-foreground'
                )}
              />
              Monitor production
            </Label>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!monitor.allowed || isRunning}
            onClick={() => runNow()}
          >
            {isRunning ? <Spinner /> : <Play />}
            Run now
          </Button>
          <p className="text-muted-foreground text-xs">{status}</p>
        </div>
        {monitor.runs.length ? (
          <ul className="divide-y border-t pt-1">
            {monitor.runs.slice(0, 5).map((run) => (
              <RunRow key={run.id} run={run} onShowFlagged={onShowFlagged} />
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}
